/**
 * ToolCallBlock — Inline tool call card in the transcript stream
 *
 * Generic design: works for any tool name (secureblob, future MCP tools).
 * Shows progress states, arguments summary, and clickable file links.
 */
import type { TranscriptEvent, FileModification } from '../../engine/agentTypes'

// Tool call event subset
type ToolCallEvent = Extract<TranscriptEvent, { type: 'tool_call' }>
type ToolResultEvent = Extract<TranscriptEvent, { type: 'tool_result' }>

interface ToolCallBlockProps {
    call: ToolCallEvent
    result?: ToolResultEvent
    onOpenFile?: (path: string) => void
}

// Smart icon based on tool name
function getToolIcon(name: string): string {
    if (name.includes('read')) return '📖'
    if (name.includes('write') || name.includes('create')) return '✏️'
    if (name.includes('delete')) return '🗑️'
    if (name.includes('search') || name.includes('find')) return '🔍'
    return '🔧'
}

function getToolLabel(name: string): { title: string; subtitle?: string } {
    if (!name.startsWith('mcp_')) {
        return { title: name, subtitle: 'SecureBlob' }
    }

    const raw = name.slice(4)
    const separator = raw.indexOf('__')
    if (separator < 0) {
        return { title: humanizeToolSegment(raw), subtitle: 'MCP' }
    }

    const server = raw.slice(0, separator)
    const tool = raw.slice(separator + 2)
    return {
        title: humanizeToolSegment(tool),
        subtitle: `MCP · ${humanizeToolSegment(server)}`,
    }
}

function summarizeArgValue(value: unknown, maxLength = 48): string {
    if (value == null) return 'null'
    if (typeof value === 'string') {
        const singleLine = value.replace(/\s+/g, ' ').trim()
        if (!singleLine) return '""'
        return singleLine.length > maxLength ? `${singleLine.slice(0, maxLength - 1).trimEnd()}…` : singleLine
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    if (Array.isArray(value)) {
        if (value.length === 0) return '[]'
        const preview = value
            .slice(0, 2)
            .map((entry) => summarizeArgValue(entry, 18))
            .join(', ')
        return value.length > 2 ? `[${preview}, +${value.length - 2}]` : `[${preview}]`
    }
    if (typeof value === 'object') {
        const keys = Object.keys(value as Record<string, unknown>)
        return keys.length === 0 ? '{}' : `{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? ', …' : ''}}`
    }
    return String(value)
}

function getPriorityForArg(name: string, key: string): number {
    const normalizedTool = name.toLowerCase()
    const normalizedKey = key.toLowerCase()
    const priorities = [
        'path',
        'query',
        'url',
        'urls',
        'prompt',
        'search_term',
        'term',
        'q',
        'numresults',
        'limit',
        'count',
        'offset',
        'include_domains',
        'exclude_domains',
        'days',
    ]
    const directIndex = priorities.indexOf(normalizedKey)
    if (directIndex >= 0) return directIndex
    if (normalizedTool.includes('search') && normalizedKey.includes('query')) return 1
    if (normalizedTool.includes('search') && normalizedKey.includes('result')) return 7
    return 100 + normalizedKey.localeCompare('')
}

function getArgsSummaryEntries(name: string, args: Record<string, unknown>): Array<{ key: string; value: string }> {
    const entries = Object.entries(args)
    if (entries.length === 0) return []

    return entries
        .sort(([a], [b]) => {
            const priorityDelta = getPriorityForArg(name, a) - getPriorityForArg(name, b)
            return priorityDelta !== 0 ? priorityDelta : a.localeCompare(b)
        })
        .slice(0, 4)
        .map(([key, value]) => ({ key, value: summarizeArgValue(value) }))
}

function formatPayload(value: unknown): string {
    if (typeof value === 'string') {
        try {
            return JSON.stringify(JSON.parse(value), null, 2)
        } catch {
            return value
        }
    }
    return JSON.stringify(value ?? null, null, 2)
}

function humanizeToolSegment(value: string): string {
    return value
        .replace(/_\d+$/, '')
        .split('_')
        .filter(Boolean)
        .join(' ')
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${Math.max(0, Math.round(ms))}ms`
    if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`
    return `${Math.round(ms / 1000)}s`
}

function formatClock(timestamp: number): string {
    return new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
    }).format(timestamp)
}

function summarizeResult(result: ToolResultEvent | undefined): string | null {
    if (!result) return null
    if (result.filesModified && result.filesModified.length > 0) {
        return `${result.filesModified.length} file${result.filesModified.length === 1 ? '' : 's'} changed`
    }
    const raw = result.result?.trim()
    if (!raw) return null
    try {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object') {
            const record = parsed as Record<string, unknown>
            if (typeof record.message === 'string') return record.message
            if (typeof record.error === 'string') return record.error
            const keys = Object.keys(record)
            if (keys.length > 0) return `Returned ${keys.length} field${keys.length === 1 ? '' : 's'}`
        }
    } catch {
        // Ignore non-JSON payloads.
    }
    return raw.length > 120 ? `${raw.slice(0, 117)}...` : raw
}

function parseStructuredToolError(result: ToolResultEvent | undefined): {
    message: string
    server?: string
    tool?: string
} | null {
    if (!result?.result) return null
    try {
        const parsed = JSON.parse(result.result)
        if (!parsed || typeof parsed !== 'object') return null
        const record = parsed as Record<string, unknown>
        const message = typeof record.message === 'string'
            ? record.message
            : typeof record.error === 'string'
                ? record.error
                : null
        if (!message) return null
        return {
            message,
            server: typeof record.server === 'string' ? record.server : undefined,
            tool: typeof record.tool === 'string' ? record.tool : undefined,
        }
    } catch {
        return null
    }
}

// Status indicator
function StatusBadge({ status }: { status: ToolCallEvent['status'] }) {
    const map = {
        pending: { icon: '○', cls: 'status-pending', label: 'Queued' },
        running: { icon: '', cls: 'status-running', label: 'Running' },
        completed: { icon: '✓', cls: 'status-completed', label: 'Done' },
        error: { icon: '✗', cls: 'status-error', label: 'Error' },
    }
    const { icon, cls, label } = map[status]
    return (
        <span className={`tool-status ${cls}`}>
            {status === 'running' ? <span className="spinner" /> : icon}
            <span>{label}</span>
        </span>
    )
}

function FileLink({ mod, onOpenFile }: { mod: FileModification; onOpenFile?: (p: string) => void }) {
    return (
        <button
            className="file-link"
            onClick={() => onOpenFile?.(mod.path)}
            title={`Open ${mod.path}`}
        >
            📄 {mod.path}
            <span className={`file-action file-action-${mod.action}`}>{mod.action}</span>
        </button>
    )
}

export function ToolCallBlock({ call, result, onOpenFile }: ToolCallBlockProps) {
    const label = getToolLabel(call.name)
    const hasDetails = Object.keys(call.args).length > 0 || Boolean(result?.result)
    const argSummaryEntries = getArgsSummaryEntries(call.name, call.args)
    const durationMs = result ? Math.max(0, result.timestamp - call.timestamp) : null
    const resultSummary = summarizeResult(result)
    const structuredError = call.status === 'error' ? parseStructuredToolError(result) : null

    return (
        <div className={`agent-tool-call tool-${call.status}`}>
            <div className="tool-header">
                <span className="tool-icon">{getToolIcon(call.name)}</span>
                <div className="tool-heading">
                    <span className="tool-name">{label.title}</span>
                    {label.subtitle && <span className="tool-subtitle">{label.subtitle}</span>}
                </div>
                <StatusBadge status={call.status} />
            </div>
            <div className="tool-meta">
                <span className="tool-meta-chip">Started {formatClock(call.timestamp)}</span>
                {durationMs !== null && <span className="tool-meta-chip">Elapsed {formatDuration(durationMs)}</span>}
                {result?.filesModified && result.filesModified.length > 0 && (
                    <span className="tool-meta-chip">{result.filesModified.length} file{result.filesModified.length === 1 ? '' : 's'} changed</span>
                )}
            </div>
            {argSummaryEntries.length > 0 && (
                <div className="tool-args" aria-label="Tool arguments summary">
                    {argSummaryEntries.map((entry) => (
                        <span key={entry.key} className="tool-arg-chip" title={`${entry.key}: ${entry.value}`}>
                            <span className="tool-arg-key">{entry.key}</span>
                            <span className="tool-arg-value">{entry.value}</span>
                        </span>
                    ))}
                    {Object.keys(call.args).length > argSummaryEntries.length && (
                        <span className="tool-arg-chip tool-arg-chip-muted">
                            <span className="tool-arg-value">+{Object.keys(call.args).length - argSummaryEntries.length} more</span>
                        </span>
                    )}
                </div>
            )}
            {resultSummary && call.status !== 'error' && (
                <div className="tool-result-summary">{resultSummary}</div>
            )}
            {structuredError && (
                <div className="tool-error-card">
                    <div className="tool-error-summary">{structuredError.message}</div>
                    <div className="tool-error-meta">
                        {structuredError.server && <span className="tool-error-chip">Server {structuredError.server}</span>}
                        {structuredError.tool && <span className="tool-error-chip">Tool {structuredError.tool}</span>}
                    </div>
                </div>
            )}
            {hasDetails && (
                <details className="tool-details" open={call.status === 'error'}>
                    <summary>Payload</summary>
                    <div className="tool-detail-grid">
                        <div className="tool-detail-panel">
                            <div className="tool-detail-label">Arguments</div>
                            <pre className="tool-detail-pre">{formatPayload(call.args)}</pre>
                        </div>
                        {result && (
                            <div className="tool-detail-panel">
                                <div className="tool-detail-label">Result</div>
                                <pre className="tool-detail-pre">{formatPayload(result.result)}</pre>
                            </div>
                        )}
                    </div>
                </details>
            )}
            {result?.filesModified && result.filesModified.length > 0 && (
                <div className="tool-files">
                    {result.filesModified.map(mod => (
                        <FileLink key={mod.path} mod={mod} onOpenFile={onOpenFile} />
                    ))}
                </div>
            )}
            {result && call.status === 'error' && !structuredError && (
                <div className="tool-error">{result.result}</div>
            )}
        </div>
    )
}
